import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input } from 'antd';
import axios from 'axios';
import { API_DOMAIN } from '../constants';
import useSnackbar from '../Snackbar/UseSnackbar';
import siteConfig from '../../siteConfig';
import { countSrmChangelogItems } from '../../utils/srmVersion';

// Update this together with siteConfig.version/build_no before each rollout.
// It is only the text area's initial value, so the publisher can freely edit
// the release notes in Global Settings before broadcasting them.
const DEFAULT_CHANGELOG = `✨ Welcome to SRM v5.0.0

🌐 The Complete Business Flow
Sales, customer payments, Operations, purchasing, physical custody, delivery and Accounts now work as one connected SRM process from enquiry to final outcome.

🚚 Complete Operations V2 Workspaces
Purchasers and delivery personnel now have dedicated, mobile-friendly workspaces for their assigned supplier visits, pickups, deliveries and collection tasks.

🗺️ Live Operations Map
Operations can view active pickup and delivery work on the map, see mapped delivery destinations and identify online drivers at a glance without relying on Shipday.

📦 Clearer Purchasing and Custody
Operations now carries Sales' selected supplier into purchasing, shows human-friendly part and supplier information, records received units and tracks where every physical part is currently held.

🤝 Secure Employee Handovers
Purchasers and delivery personnel can offer physical parts to another employee, with custody changing only after the recipient accepts the handover.

📍 Guided Field Execution
Assigned workers can record arrival, confirm pickups, complete non-delivery work, report failed visits or customer refusal and escalate problems with location and photo evidence where required.

🔁 Flexible Task Assignment
Dispatchers can change the responsible purchaser or delivery person on unfinished logistics tasks with a recorded reason and safe concurrency checks.

🛡️ Problems, Warranty and Recovery
The Operations drawer now supports customer changes, warranty cases, residual inventory decisions and a dedicated queue for retrying failed Payments-to-Operations updates.

💰 Connected Operations Finance
Operations can see authoritative Payments V2 obligations and record assigned cash, POS and cheque collections without creating a separate financial truth.

🧭 Simpler Operations Language
Technical workflow terms have been replaced with clearer instructions, actionable blockers and understandable next steps throughout the dispatcher and order drawer.`;

export function UpdateNotificationPanel() {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const { SuccessSnackbar, ErrorSnackbar } = useSnackbar();

  const [changelog, setChangelog] = useState(DEFAULT_CHANGELOG);
  const [requireLogout, setRequireLogout] = useState(false);
  const [pushing, setPushing] = useState(false);
  const changeCount = countSrmChangelogItems(changelog);

  const handlePush = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${API_DOMAIN}/whatsapp/push_new_version_event`, {
        changelog,
        version: siteConfig.version,
        build_no: siteConfig.build_no,
        published_at: new Date().toISOString(),
        require_logout: requireLogout,
        change_count: changeCount,
      }, head);
      if (res.data.success) {
        SuccessSnackbar("Update notification pushed!");
        setChangelog(DEFAULT_CHANGELOG);
        setRequireLogout(false);
      } else {
        ErrorSnackbar(res.data.message);
      }
    } catch (error) {
      ErrorSnackbar(error.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div style={{ fontSize: 12, color: "#595959", marginBottom: 10 }}>
          Publishing <strong>SRM v{siteConfig.version}</strong> · build <strong>{siteConfig.build_no}</strong>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Changelog</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          Description of the latest software update — shown to users in the update notification.
        </div>
        <Input.TextArea
          rows={8}
          value={changelog}
          onChange={e => setChangelog(e.target.value)}
          placeholder="What's new in this update…"
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>
          {changeCount > 0 ? `${changeCount} update ${changeCount === 1 ? "item" : "items"} detected for the banner.` : "The banner will use a generic update message."}
        </div>
      </div>
      <Checkbox checked={requireLogout} onChange={event => setRequireLogout(event.target.checked)}>
        Require users to log in again after loading this update
      </Checkbox>
      {requireLogout && (
        <div style={{ marginTop: -6, fontSize: 12, color: "#8c8c8c" }}>
          Users will be warned that loading this version clears their current session and redirects them to the login screen.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" loading={pushing} disabled={!changelog.trim()} onClick={handlePush}>
          Push update notif
        </Button>
      </div>
    </div>
  );
}
